import React, { FunctionComponent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import MaterialTable, { MaterialTableProps } from 'material-table';
import Moment from 'moment';
import DocumentTypeEntity from '../../../entities/DocumentType.Entity';
import ReferralDocumentEntity from '../../../entities/ReferralDocument.Entity';
import styles from './AttachDocumentsSubmitModal.Styles';

const ADDITIONAL_COMMENTS_MAX = 2500;

export interface SiuSubmitReferralPayload {
    documents: Array<ReferralDocumentEntity>;
    rushRequest: boolean;
    additionalComments: string;
}

interface IAttachDocumentsSubmitModalProps {
    open: boolean;
    loading: boolean;
    documentTypes: Array<DocumentTypeEntity>;
    availableDocuments: Array<ReferralDocumentEntity>;
    onClose: () => void;
    /** Legacy confirmation step: skip attach and submit (unused when skipConfirmationStep). */
    onNoSubmit?: () => void;
    /** Load document type checkboxes only (no claim documents). Used when skipConfirmationStep. */
    onLoadDocumentTypes: () => void;
    /** Load claim documents for the selected document type IDs. Pass dates when any selected type has isDateFilterRequired. */
    onSearchDocuments: (
        selectedDocumentTypeIds: Array<number>,
        dateFrom: Date | null,
        dateTo: Date | null,
    ) => void;
    /** When confirmation step is used, "Attach Documents" can load types + documents in one call. */
    onLoadExpandedView?: () => void;
    onSubmitWithDocuments: (payload: SiuSubmitReferralPayload) => void;
    /** When true, skip the confirmation step and open directly on the document picker (SIU submit). */
    skipConfirmationStep?: boolean;
}

const TableContainer: React.FunctionComponent = ({ children }) => <>{children}</>;

export const AttachDocumentsSubmitModal: FunctionComponent<IAttachDocumentsSubmitModalProps> = (
    props: IAttachDocumentsSubmitModalProps,
) => {
    const { skipConfirmationStep = false, onLoadDocumentTypes, open } = props;

    const classes = styles();
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [selectedDocumentTypeIds, setSelectedDocumentTypeIds] = useState<Array<number>>([]);
    const [selectedDocuments, setSelectedDocuments] = useState<Array<ReferralDocumentEntity>>([]);
    const [rushRequest, setRushRequest] = useState(false);
    const [additionalComments, setAdditionalComments] = useState('');
    const [dateRangeFrom, setDateRangeFrom] = useState('');
    const [dateRangeTo, setDateRangeTo] = useState('');
    const directOpenLoadRequestedRef = useRef(false);

    useEffect(() => {
        if (!open) {
            setIsExpanded(false);
            setSelectedDocumentTypeIds([]);
            setSelectedDocuments([]);
            setRushRequest(false);
            setAdditionalComments('');
            setDateRangeFrom('');
            setDateRangeTo('');
            directOpenLoadRequestedRef.current = false;
        }
    }, [open]);

    useLayoutEffect(() => {
        if (!open || !skipConfirmationStep) {
            return;
        }
        setIsExpanded(true);
        if (!directOpenLoadRequestedRef.current) {
            directOpenLoadRequestedRef.current = true;
            setSelectedDocumentTypeIds([]);
            setSelectedDocuments([]);
            onLoadDocumentTypes();
        }
    }, [open, skipConfirmationStep, onLoadDocumentTypes]);

    const orderedDocumentTypes = useMemo(
        () =>
            [...(props.documentTypes ?? [])].sort(
                (a, b) => (a.orderToDisplay ?? Number.MAX_SAFE_INTEGER) - (b.orderToDisplay ?? Number.MAX_SAFE_INTEGER),
            ),
        [props.documentTypes],
    );

    const dateFilterAnchorDocumentTypeId = useMemo(() => {
        const selectedOfGroup = orderedDocumentTypes.filter(
            (t) => !!t.isDateFilterRequired && selectedDocumentTypeIds.includes(t.documentTypeId),
        );
        if (selectedOfGroup.length === 0) {
            return null;
        }
        return selectedOfGroup[selectedOfGroup.length - 1].documentTypeId;
    }, [orderedDocumentTypes, selectedDocumentTypeIds]);

    const selectionRequiresDateFilter = useMemo(
        () =>
            orderedDocumentTypes.some(
                (t) => !!t.isDateFilterRequired && selectedDocumentTypeIds.includes(t.documentTypeId),
            ),
        [orderedDocumentTypes, selectedDocumentTypeIds],
    );

    useEffect(() => {
        if (dateFilterAnchorDocumentTypeId == null) {
            setDateRangeFrom('');
            setDateRangeTo('');
        }
    }, [dateFilterAnchorDocumentTypeId]);

    const selectedDocumentNumberSet = useMemo(
        () => new Set((selectedDocuments ?? []).map((d) => d.documentNumber)),
        [selectedDocuments],
    );

    const tableData = useMemo(
        () =>
            (props.availableDocuments ?? []).map((doc) => ({
                ...doc,
                tableData: {
                    ...(doc as any).tableData,
                    checked: selectedDocumentNumberSet.has(doc.documentNumber),
                },
            })),
        [props.availableDocuments, selectedDocumentNumberSet],
    );

    const resetExpandedState = () => {
        setSelectedDocumentTypeIds([]);
        setSelectedDocuments([]);
        setDateRangeFrom('');
        setDateRangeTo('');
    };

    const handleClose = () => {
        setIsExpanded(false);
        resetExpandedState();
        props.onClose();
    };

    const handleNoSubmit = () => {
        setIsExpanded(false);
        resetExpandedState();
        props.onNoSubmit?.();
    };

    const handleYes = () => {
        setIsExpanded(true);
        resetExpandedState();
        if (props.onLoadExpandedView) {
            props.onLoadExpandedView();
        } else {
            onLoadDocumentTypes();
        }
    };

    const handleBack = () => {
        if (skipConfirmationStep) {
            handleClose();
            return;
        }
        setIsExpanded(false);
        resetExpandedState();
    };

    const handleSelectAll = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        const allIds = checked ? orderedDocumentTypes.map((type) => type.documentTypeId) : [];
        setSelectedDocumentTypeIds(allIds);
        if (!checked) {
            setSelectedDocuments([]);
        }
    };

    const handleSelectDocumentType = (documentTypeId: number, checked: boolean) => {
        const updatedIds = checked
            ? [...selectedDocumentTypeIds, documentTypeId]
            : selectedDocumentTypeIds.filter((id) => id !== documentTypeId);

        setSelectedDocumentTypeIds(updatedIds);

        if (updatedIds.length === 0) {
            setSelectedDocuments([]);
            return;
        }

        const filterDocuments = (previousSelected: Array<ReferralDocumentEntity>) =>
            previousSelected.filter((document) => updatedIds.includes(document.documentTypeId));

        setSelectedDocuments(filterDocuments);
    };

    const handleSearchDocuments = () => {
        let dateFrom: Date | null = null;
        let dateTo: Date | null = null;
        if (selectionRequiresDateFilter) {
            if (dateRangeFrom) {
                dateFrom = new Date(`${dateRangeFrom}T00:00:00`);
            }
            if (dateRangeTo) {
                dateTo = new Date(`${dateRangeTo}T00:00:00`);
            }
        }
        props.onSearchDocuments(selectedDocumentTypeIds, dateFrom, dateTo);
    };

    const handleAdditionalCommentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        if (next.length <= ADDITIONAL_COMMENTS_MAX) {
            setAdditionalComments(next);
        }
    };

    const renderDateRangeFields = (currentDocumentTypeId: number) => {
        if (currentDocumentTypeId !== dateFilterAnchorDocumentTypeId) {
            return null;
        }

        return (
            <div className={classes.dateRangeBlock}>
                <div className={classes.dateRangeFieldWrap}>
                    <Typography component="label" htmlFor="siu-attach-docs-date-from" className={classes.dateRangeFieldLabel}>
                        Date Range From
                    </Typography>
                    <TextField
                        id="siu-attach-docs-date-from"
                        className={classes.dateRangeInput}
                        type="date"
                        value={dateRangeFrom}
                        onChange={(e) => setDateRangeFrom(e.target.value)}
                        disabled={props.loading}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </div>
                <div className={classes.dateRangeFieldWrap}>
                    <Typography component="label" htmlFor="siu-attach-docs-date-to" className={classes.dateRangeFieldLabel}>
                        To
                    </Typography>
                    <TextField
                        id="siu-attach-docs-date-to"
                        className={classes.dateRangeInput}
                        type="date"
                        value={dateRangeTo}
                        onChange={(e) => setDateRangeTo(e.target.value)}
                        disabled={props.loading}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </div>
            </div>
        );
    };

    const tableProps: MaterialTableProps<ReferralDocumentEntity> = {
        columns: [
            { title: 'Description', field: 'fileDescription' },
            { title: 'File Name', field: 'fileName' },
            { title: 'Document Type', field: 'documentType' },
            {
                title: 'Date',
                render: (rowData) => (rowData.dueDate ? Moment(rowData.dueDate).format('M/D/YYYY') : ''),
            },
        ],
        data: tableData,
        options: {
            selection: true,
            paging: false,
            toolbar: false,
            maxBodyHeight: 220,
            minBodyHeight: 220,
            headerStyle: {
                paddingTop: 0,
                paddingBottom: 0,
                height: 16,
                minHeight: 16,
                fontSize: '13px',
                lineHeight: '14px',
            },
            selectionProps: (rowData: ReferralDocumentEntity) => ({
                disabled: !selectedDocumentTypeIds.includes(rowData.documentTypeId),
                color: 'secondary',
            }),
            rowStyle: (rowData: ReferralDocumentEntity) =>
                selectedDocumentTypeIds.includes(rowData.documentTypeId) ? undefined : { opacity: 0.6 },
        },
        onSelectionChange: (rows) =>
            setSelectedDocuments(
                (rows ?? []).filter((d) => selectedDocumentTypeIds.includes(d.documentTypeId)),
            ),
    };

    const isAllSelected =
        orderedDocumentTypes.length > 0 && selectedDocumentTypeIds.length === orderedDocumentTypes.length;

    const expandedSiuContent = skipConfirmationStep && isExpanded;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth={isExpanded ? 'md' : 'sm'}
            PaperProps={{
                style: isExpanded
                    ? { borderRadius: 4 }
                    : { width: 530, maxWidth: 530, borderRadius: 4 },
            }}
        >
            {expandedSiuContent ? (
                <DialogTitle className={classes.siuExpandedTitle} disableTypography>
                    <Typography component="span" variant="h6" className={classes.siuExpandedTitleText}>
                        Additional Comments and Documents
                    </Typography>
                    <IconButton aria-label="close" className={classes.siuExpandedCloseButton} onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
            ) : !isExpanded ? (
                <DialogTitle className={classes.title}>
                    Attach Documents
                    <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
            ) : (
                <IconButton aria-label="close" className={classes.closeButtonExpanded} onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            )}

            <DialogContent
                className={isExpanded ? classes.expandedDialogContent : undefined}
                style={isExpanded ? undefined : { padding: '0 32px 16px 32px' }}
            >
                {!isExpanded ? (
                    <Typography variant="body1" className={classes.initialDescription}>
                        Would you like to attach documents to this referral before submitting?
                    </Typography>
                ) : expandedSiuContent ? (
                    <>
                        <FormControlLabel
                            className={classes.rushRow}
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={rushRequest}
                                    onChange={(_e, c) => setRushRequest(c)}
                                    disabled={props.loading}
                                />
                            }
                            label="This is a Rush Request"
                        />

                        <Typography variant="subtitle1" className={classes.sectionTitleTeal}>
                            Additional Comments
                        </Typography>
                        <div className={classes.commentsFieldWrap}>
                            <TextField
                                multiline
                                rows={5}
                                fullWidth
                                variant="outlined"
                                value={additionalComments}
                                onChange={handleAdditionalCommentsChange}
                                disabled={props.loading}
                                inputProps={{ maxLength: ADDITIONAL_COMMENTS_MAX }}
                                className={classes.commentsTextField}
                                placeholder="Use this space to note any special instructions or context for the referral. Include preferred vendor (if applicable), specific investigation objectives, key claimant details, deadlines, or any other information needed to support the assignment."
                            />
                            <Typography variant="caption" className={classes.charCounter}>
                                {additionalComments.length} / {ADDITIONAL_COMMENTS_MAX}
                            </Typography>
                        </div>

                        <div className={classes.documentsSectionHeader}>
                            <Typography variant="subtitle1" className={classes.documentsSectionTitle}>
                                Documents
                            </Typography>
                        </div>

                        <FormControlLabel
                            className={`${classes.filterLabel} ${classes.selectAllLabel}`}
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={isAllSelected}
                                    onChange={handleSelectAll}
                                    disabled={props.loading || orderedDocumentTypes.length === 0}
                                />
                            }
                            label="Select All"
                        />

                        <div className={classes.documentTypesColumnFlow}>
                            {orderedDocumentTypes.map((documentType) => (
                                <div key={documentType.documentTypeId} className={classes.documentTypesColumnItem}>
                                    <FormControlLabel
                                        className={`${classes.filterLabel} ${classes.documentTypeLabel}`}
                                        control={
                                            <Checkbox
                                                color="secondary"
                                                checked={selectedDocumentTypeIds.includes(documentType.documentTypeId)}
                                                onChange={(_event, checked) =>
                                                    handleSelectDocumentType(documentType.documentTypeId, checked)
                                                }
                                                disabled={props.loading}
                                            />
                                        }
                                        label={documentType.documentTypeName}
                                    />
                                    {renderDateRangeFields(documentType.documentTypeId)}
                                </div>
                            ))}
                        </div>

                        <div className={classes.searchDocumentsRow}>
                            <Button
                                variant="outlined"
                                className={`${classes.secondaryButton} ${classes.searchDocumentsButton}`}
                                onClick={handleSearchDocuments}
                                disabled={
                                    props.loading ||
                                    orderedDocumentTypes.length === 0 ||
                                    selectedDocumentTypeIds.length === 0
                                }
                            >
                                Search Documents
                            </Button>
                        </div>

                        <Typography variant="subtitle2" className={classes.tableTitle}>
                            Available Documents
                        </Typography>

                        <div className={classes.tableContainer}>
                            {props.loading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <MaterialTable
                                    components={{
                                        Container: TableContainer,
                                    }}
                                    {...tableProps}
                                />
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <div className={classes.documentsSectionHeader}>
                            <Typography variant="subtitle1" className={classes.documentsSectionTitle}>
                                Documents
                            </Typography>
                        </div>

                        <div className={classes.filtersContainer}>
                            <FormControlLabel
                                className={`${classes.filterLabel} ${classes.selectAllLabel}`}
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                        disabled={props.loading || orderedDocumentTypes.length === 0}
                                    />
                                }
                                label="Select All"
                            />

                            {orderedDocumentTypes.map((documentType) => (
                                <FormControlLabel
                                    key={documentType.documentTypeId}
                                    className={`${classes.filterLabel} ${classes.documentTypeLabel}`}
                                    control={
                                        <Checkbox
                                            color="secondary"
                                            checked={selectedDocumentTypeIds.includes(documentType.documentTypeId)}
                                            onChange={(_event, checked) =>
                                                handleSelectDocumentType(documentType.documentTypeId, checked)
                                            }
                                            disabled={props.loading}
                                        />
                                    }
                                    label={documentType.documentTypeName}
                                />
                            ))}
                        </div>

                        <Typography variant="subtitle2" className={classes.tableTitle}>
                            Available Documents
                        </Typography>

                        <div className={classes.tableContainer}>
                            {props.loading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <MaterialTable
                                    components={{
                                        Container: TableContainer,
                                    }}
                                    {...tableProps}
                                />
                            )}
                        </div>
                    </>
                )}
            </DialogContent>

            <DialogActions className={classes.footerActionsBar}>
                {!isExpanded ? (
                    <>
                        <Button
                            onClick={handleNoSubmit}
                            variant="outlined"
                            className={classes.secondaryButton}
                            disabled={props.loading}
                        >
                            Skip & Submit
                        </Button>
                        <Button
                            onClick={handleYes}
                            variant="contained"
                            color="primary"
                            className={classes.primaryButton}
                            disabled={props.loading}
                        >
                            Attach Documents
                        </Button>
                    </>
                ) : skipConfirmationStep ? (
                    <>
                        <Button
                            onClick={handleBack}
                            variant="outlined"
                            className={classes.secondaryButton}
                            disabled={props.loading}
                        >
                            Back
                        </Button>
                        <Button
                            onClick={() =>
                                props.onSubmitWithDocuments({
                                    documents: selectedDocuments,
                                    rushRequest,
                                    additionalComments,
                                })
                            }
                            variant="contained"
                            color="primary"
                            className={classes.primaryButton}
                            disabled={props.loading}
                        >
                            Submit Referral
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            onClick={handleBack}
                            variant="outlined"
                            className={classes.secondaryButton}
                            disabled={props.loading}
                        >
                            Back
                        </Button>
                        <Button
                            onClick={() =>
                                props.onSubmitWithDocuments({
                                    documents: selectedDocuments,
                                    rushRequest: false,
                                    additionalComments: '',
                                })
                            }
                            variant="contained"
                            color="primary"
                            className={classes.primaryButton}
                            disabled={props.loading || selectedDocuments.length === 0}
                        >
                            Submit with Documents
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};
